import { ServiceAction } from "./service-part-action"

/**
 * Whether a component's work has to be done now for the bike to be safe in
 * traffic, or could wait another six months. The customer's proposal offers
 * two presets built from it: "Trafiksäker" (only NOW) and "Toppskick"
 * (everything).
 */
export enum PartActionUrgency {
  NOW = "NOW",
  CAN_WAIT = "CAN_WAIT",
}

/**
 * Diagnostic actions: what they find is unknown until they're done, so there
 * is no wear to judge urgency by. They're always done during this visit.
 */
const ALWAYS_NOW_ACTIONS: ServiceAction[] = [
  ServiceAction.CHECK,
  ServiceAction.TROUBLESHOOT,
]

export class PartActionUrgencyUtils {
  /**
   * The urgency of a component as the inspection shows it: the mechanic's
   * pick, or "Nu" when they haven't picked one.
   */
  static forPart(part: {
    urgency: PartActionUrgency | null
    isMandatory: boolean
    recommendedAction: ServiceAction | null
  }): PartActionUrgency {
    return PartActionUrgencyUtils.resolve(
      part.urgency ?? PartActionUrgency.NOW,
      part,
    )
  }

  /** Whether the mechanic gets to pick the urgency for this work. */
  static isPickable(part: {
    isMandatory: boolean
    recommendedAction: ServiceAction | null
  }): boolean {
    if (part.isMandatory) return false
    if (part.recommendedAction === null) return true
    return !ALWAYS_NOW_ACTIONS.includes(part.recommendedAction)
  }

  /**
   * Mandatory and diagnostic work is never something the customer can
   * postpone.
   */
  static resolve(
    urgency: PartActionUrgency,
    part: { isMandatory: boolean; recommendedAction: ServiceAction | null },
  ): PartActionUrgency {
    return PartActionUrgencyUtils.isPickable(part)
      ? urgency
      : PartActionUrgency.NOW
  }
}
